import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { ProductCategory } from "@/lib/category-service";
import { ChevronDown, ChevronUp } from "lucide-react";

interface SubCategoryCardProps {
  id: string;
  categoryId: string;
  categorySlug: string; // Add categorySlug field
  slug: string; // Add slug field
  name: string;
  image: string;
  productCategories: ProductCategory[];
  description?: string; // Make optional since we're not using it
}

import { getCloudinaryUrl } from "@/lib/cloudinary";

export default function SubCategoryCard({
  categorySlug,
  slug,
  name,
  image,
  productCategories,
}: SubCategoryCardProps) {
  // State to control expanded view
  const [expanded, setExpanded] = useState(false);
  
  // Determine which product categories to display
  const displayedCategories = expanded ? productCategories : productCategories.slice(0, 4);
  
  return (
    <Card className="overflow-hidden transition-all duration-300 hover:shadow-md border-none h-full bg-transparent hover:bg-white/50">
      <div className="flex flex-row w-full p-5 gap-8">
        {/* Left side - Subcategory */}
        <div className="w-[180px] h-[180px] bg-gray-50 rounded-lg p-3 flex flex-col items-center text-center">
          <Link to={`/${categorySlug}/${slug}`} className="group flex flex-col items-center h-full w-full justify-between">
            <div className="relative w-20 h-20 overflow-hidden mt-2">
              <img
                src={getCloudinaryUrl(image, 180, 180)}
                srcSet={`
                  ${getCloudinaryUrl(image, 64, 64)} 64w,
                  ${getCloudinaryUrl(image, 128, 128)} 128w,
                  ${getCloudinaryUrl(image, 180, 180)} 180w
                `}
                sizes="(max-width: 600px) 64px, 128px, 180px"
                alt={name}
                className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-110"
              />
              
              <div className="absolute bottom-0 right-0 bg-primary/80 text-white text-xs font-medium px-2 py-1 rounded-tl-md">
                {productCategories.length}
              </div>
            </div>
            
            <div className="w-full">
              <h3 className="text-sm font-medium line-clamp-2 text-gray-800 group-hover:text-primary transition-colors duration-200">
                {name}
              </h3>
            </div>
          </Link>
        </div>
        
        {/* Right side - Product Categories */}
        <div className="flex-grow">
          <div className="flex items-center mb-2">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Product Categories</span>
            <div className="ml-2 h-px bg-gray-200 flex-grow"></div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {displayedCategories.map((category, index) => (
              <Link 
                key={category.id}
                to={`/${categorySlug}/${slug}/${category.slug || category.id}`}
                className="group flex flex-col items-center"
              >
                <div className="w-[130px] h-[130px] bg-white rounded-lg p-2 flex flex-col items-center justify-between border border-gray-100 hover:border-gray-200 transition-colors">
                  <div className="relative w-16 h-16 overflow-hidden mt-2">
                    <img
                      src={getCloudinaryUrl(category.image_url || `/placeholder-product-category-${index % 4 + 1}.jpg`, 64, 64)}
                      srcSet={`
                        ${getCloudinaryUrl(category.image_url || `/placeholder-product-category-${index % 4 + 1}.jpg`, 64, 64)} 64w,
                        ${getCloudinaryUrl(category.image_url || `/placeholder-product-category-${index % 4 + 1}.jpg`, 128, 128)} 128w,
                        ${getCloudinaryUrl(category.image_url || `/placeholder-product-category-${index % 4 + 1}.jpg`, 180, 180)} 180w
                      `}
                      sizes="(max-width: 600px) 64px, 128px, 180px"
                      alt={category.name}
                      className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-110"
                    />
                  </div>
                  <span className="text-xs text-center font-medium line-clamp-2 group-hover:text-primary transition-colors w-full">
                    {category.name}
                  </span>
                </div>
              </Link>
            ))}
          </div>
          
          {productCategories.length > 4 && (
            <div className="mt-4 flex justify-center">
              <button 
                onClick={() => setExpanded(!expanded)}
                className="flex items-center gap-1 text-sm text-primary hover:underline px-4 py-2 rounded-full bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                {expanded ? (
                  <>
                    <ChevronUp className="h-4 w-4" />
                    Show less
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4" />
                    View all {productCategories.length} categories
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
